import type { EntryPoint, EntryPointComponent, PreloadFetchPolicy } from 'react-relay';

import type { IdentityUserAri } from '@atlassian/ari/identity/user';
import type { PlatformSiteAri } from '@atlassian/ari/platform/site';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import parameters, {
	type HomeAssignedListQuery,
} from './assigned-list/__generated__/HomeAssignedListQuery.graphql';
import {
	createJiraIssueCypherQuery,
	createPullRequestCypherQuery,
	createTownsquareCypherQuery,
} from './assigned-list/cypher-query-builders';
import type { ExtraProps, RuntimeProps } from './assigned-list/types';

export type EntryPointParams = {
	/**
	 * The user whose assigned items are fetched. Also used to filter out items the user is no longer
	 * responsible for (for example, a reassigned Jira issue).
	 */
	currentUser: IdentityUserAri;
	/**
	 * Site ARI used to unit scope results.
	 */
	unitId: PlatformSiteAri;
	/**
	 * Number of results to fetch per entity type. Defaults to 25.
	 */
	first?: number;
	/**
	 * When set, items are grouped by "smart" categories, instead of comingled by last updated
	 * - pull requests by reviewing / authored,
	 * - work items by status,
	 * - projects and goals together
	 */
	enableSmartGrouping?: boolean;
};

const DEFAULT_LIMIT = 25;

const fetchPolicy: PreloadFetchPolicy = 'store-and-network';

type HomeAssignedListEntryPointComponent = EntryPointComponent<
	{
		queryReference: HomeAssignedListQuery;
	},
	Record<never, never>,
	RuntimeProps,
	ExtraProps
>;

export const homeAssignedListEntryPoint: EntryPoint<
	HomeAssignedListEntryPointComponent,
	EntryPointParams
> = createEntryPoint({
	root: JSResourceForInteraction<HomeAssignedListEntryPointComponent>(() => import(/* webpackChunkName: "@atlassian/home-assigned-entrypoint" */ "./assigned-list/HomeAssignedListQuery"), { moduleId: "9601c191", moduleName: "HomeAssignedListQuery", modulePath: "../platform/packages/townsquare/home-assigned/src/assigned-list/HomeAssignedListQuery.tsx", priority: "interaction", moduleId: 'homeAssignedListEntryPoint' }),
	getPreloadProps: ({ currentUser, unitId, first, enableSmartGrouping }: EntryPointParams) => {
		const accountId = currentUser.resourceId;

		return {
			queries: {
				queryReference: {
					parameters,
					variables: {
						townsquareQuery: createTownsquareCypherQuery(accountId),
						jiraIssueQuery: createJiraIssueCypherQuery(accountId),
						pullRequestQuery: createPullRequestCypherQuery(accountId, enableSmartGrouping),
						queryContext: unitId.toString(),
						first: first ?? DEFAULT_LIMIT,
					},
					options: {
						fetchPolicy,
					},
				},
			},
			extraProps: {
				currentUser,
				enableSmartGrouping,
			},
		};
	},
});
